import {
  KYDOrganizationFee,
  KYDFeeBreak,
} from "@common/interfaces/KYDOrganization";

export interface PreviewData {
  faceValue: number;
  fees: number;
  rebate: number;
  finalPrice: number;
}

/**
 * Calculate preview data for rebate display using the backend API.
 * This function calls the calculateFinalTicketPrice API with a rebate override
 * to get accurate fee calculations.
 *
 * @param rebate - Rebate object with amount in bips (basis points)
 * @param calculateFinalTicketPrice - Function to call the API
 * @param event_id - Optional event ID for event-level calculations
 * @param fallbackCalculateFees - Optional fallback function if API fails
 * @returns Promise of preview data array for face values [10, 20, 50]
 */
export const calculatePreviewData = async (
  rebate: { amount: number },
  calculateFinalTicketPrice: (
    price: number,
    event_id?: string,
    fee_overrides?: { type: string; amount: number }
  ) => Promise<{
    total_price: { getAmount: () => number };
    ticket_fees: Array<{
      type: string;
      price: { amount: number; units: number };
    }>;
  }>,
  event_id?: string,
  fallbackCalculateFees?: (faceValue: number) => Promise<number>
): Promise<PreviewData[]> => {
  // Face values in dollars, convert to cents (integers)
  const FACE_VALUES_DOLLARS = [10, 20, 50];
  const FACE_VALUES_CENTS = FACE_VALUES_DOLLARS.map((v) => v * 100);

  return Promise.all(
    FACE_VALUES_CENTS.map(async (faceValueCents, index) => {
      const faceValueDollars = FACE_VALUES_DOLLARS[index];
      try {
        // Pass rebate override to get accurate calculation from backend
        // API expects price in dollars, but we'll work in cents internally
        const { ticket_fees, total_price } = await calculateFinalTicketPrice(
          faceValueDollars,
          event_id,
          {
            type: "rebate",
            amount: rebate.amount, // amount is in bips
          }
        );

        // Extract rebate and non-rebate fees from ticket_fees
        // Use price.amount (cents) instead of price.units (dollars) to keep integers
        let rebateAmountCents = 0;
        let nonRebateFeesCents = 0;

        for (const fee of ticket_fees) {
          if (fee.type === "rebate") {
            rebateAmountCents = fee.price.amount; // amount is in cents (integer)
          } else {
            // Sum all non-rebate fees (amount is in cents)
            nonRebateFeesCents += fee.price.amount;
          }
        }

        // total_price.getAmount() returns cents (integer), no division needed
        const finalPriceCents = total_price.getAmount();

        return {
          faceValue: faceValueCents, // in cents (integer)
          fees: nonRebateFeesCents, // in cents (integer)
          rebate: rebateAmountCents, // in cents (integer)
          finalPrice: finalPriceCents, // in cents (integer)
        };
      } catch (error) {
        console.error("Error calculating preview data:", error);
        // Fallback: use fallbackCalculateFees if provided, otherwise return 0 values
        if (fallbackCalculateFees) {
          const calculatedFeesDollars = await fallbackCalculateFees(
            faceValueDollars
          );
          const calculatedFeesCents = Math.round(calculatedFeesDollars * 100);
          const rebatePercentage = rebate.amount / 100; // Convert bips to percentage
          const rebateAmountCents = Math.round(
            (faceValueCents * rebatePercentage) / 100
          );
          const finalPriceCents =
            faceValueCents + calculatedFeesCents + rebateAmountCents;

          return {
            faceValue: faceValueCents, // in cents (integer)
            fees: calculatedFeesCents, // in cents (integer)
            rebate: rebateAmountCents, // in cents (integer)
            finalPrice: finalPriceCents, // in cents (integer)
          };
        }
        // Return with 0 values if no fallback
        return {
          faceValue: faceValueCents, // in cents (integer)
          fees: 0,
          rebate: 0,
          finalPrice: faceValueCents,
        };
      }
    })
  );
};
